import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adminService, authService } from '../services/api';
import toast from 'react-hot-toast';

const tokens = {
  colors: {
    primary: '#1e514b',
    primaryLight: '#2a6b63',
    cream: '#faf8f5',
    warmWhite: '#fffdf9',
    charcoal: '#1a1a1a',
    slate: '#4a4a4a',
    muted: '#8a8a8a',
    border: '#e8e4de',
    success: '#059669',
    successBg: '#ecfdf5',
    warning: '#d97706',
    warningBg: '#fffbeb',
    error: '#dc3545',
    errorBg: '#fdf2f2',
  },
  fonts: {
    display: '"Playfair Display", Georgia, serif',
    body: '"DM Sans", -apple-system, sans-serif',
  },
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${tokens.colors.cream};
  font-family: ${tokens.fonts.body};
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid ${tokens.colors.border};
  padding: 1.25rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  h1 {
    font-family: ${tokens.fonts.display};
    font-size: 1.5rem;
    font-weight: 500;
    color: ${tokens.colors.charcoal};
    margin: 0;
  }
  p {
    font-size: 0.85rem;
    color: ${tokens.colors.muted};
    margin: 0.25rem 0 0;
  }
`;

const BackLink = styled.button`
  background: none;
  border: 1px solid ${tokens.colors.border};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-family: ${tokens.fonts.body};
  font-size: 0.9rem;
  color: ${tokens.colors.slate};
  cursor: pointer;
  &:hover { background: ${tokens.colors.cream}; }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
`;

const StatusTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const StatusTab = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${p => p.$active ? tokens.colors.primary : tokens.colors.border};
  border-radius: 20px;
  background: ${p => p.$active ? tokens.colors.primary : 'white'};
  color: ${p => p.$active ? 'white' : tokens.colors.slate};
  font-family: ${tokens.fonts.body};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  span {
    margin-left: 0.35rem;
    opacity: 0.7;
  }

  &:hover {
    border-color: ${tokens.colors.primary};
  }
`;

const SearchRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.625rem 1rem;
  border: 1px solid ${tokens.colors.border};
  border-radius: 8px;
  font-family: ${tokens.fonts.body};
  font-size: 0.9rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${tokens.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${tokens.colors.border};
  background: ${tokens.colors.cream};
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: ${tokens.colors.charcoal};
  border-bottom: 1px solid ${tokens.colors.border};
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;

  ${p => p.$status === 'active' && `
    background: ${tokens.colors.successBg};
    color: ${tokens.colors.success};
  `}
  ${p => p.$status === 'pending' && `
    background: ${tokens.colors.warningBg};
    color: ${tokens.colors.warning};
  `}
  ${p => p.$status === 'suspended' && `
    background: ${tokens.colors.errorBg};
    color: ${tokens.colors.error};
  `}
`;

const TierBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${tokens.colors.cream};
  color: ${tokens.colors.slate};
  border: 1px solid ${tokens.colors.border};
  text-transform: capitalize;
`;

const ActionBtn = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-family: ${tokens.fonts.body};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid;
  margin-right: 0.35rem;

  ${p => p.$variant === 'activate' && `
    background: ${tokens.colors.successBg};
    color: ${tokens.colors.success};
    border-color: ${tokens.colors.success};
    &:hover { background: ${tokens.colors.success}; color: white; }
  `}
  ${p => p.$variant === 'suspend' && `
    background: ${tokens.colors.errorBg};
    color: ${tokens.colors.error};
    border-color: ${tokens.colors.error};
    &:hover { background: ${tokens.colors.error}; color: white; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TierSelect = styled.select`
  padding: 0.3rem 0.5rem;
  border: 1px solid ${tokens.colors.border};
  border-radius: 6px;
  font-family: ${tokens.fonts.body};
  font-size: 0.8rem;
  background: white;
  margin-right: 0.35rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${tokens.colors.muted};
  font-size: 0.95rem;
`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, active: 0, suspended: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      if (search) params.search = search;
      const data = await adminService.getUsers(params);
      setUsers(data.users);
      setCounts(data.counts);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/events');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, search, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleActivate = async (userId, tier) => {
    setActionLoading(userId);
    try {
      await adminService.activateUser(userId, tier);
      toast.success('User activated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Activation failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Suspend this user? They will lose access immediately.')) return;
    setActionLoading(userId);
    try {
      await adminService.suspendUser(userId);
      toast.success('User suspended');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Suspension failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTierChange = async (userId, tier) => {
    setActionLoading(userId);
    try {
      await adminService.updateTier(userId, tier);
      toast.success(`Tier updated to ${tier}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Tier update failed');
    } finally {
      setActionLoading(null);
    }
  };

  const currentUser = authService.getCurrentUser();

  return (
    <PageWrapper>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <Header>
        <HeaderLeft>
          <h1>Admin Dashboard</h1>
          <p>Manage user accounts and subscriptions</p>
        </HeaderLeft>
        <BackLink onClick={() => navigate('/events')}>Back to Events</BackLink>
      </Header>

      <Container>
        <StatusTabs>
          <StatusTab $active={!filter} onClick={() => setFilter('')}>
            All<span>{counts.pending + counts.active + counts.suspended}</span>
          </StatusTab>
          <StatusTab $active={filter === 'pending'} onClick={() => setFilter('pending')}>
            Pending<span>{counts.pending}</span>
          </StatusTab>
          <StatusTab $active={filter === 'active'} onClick={() => setFilter('active')}>
            Active<span>{counts.active}</span>
          </StatusTab>
          <StatusTab $active={filter === 'suspended'} onClick={() => setFilter('suspended')}>
            Suspended<span>{counts.suspended}</span>
          </StatusTab>
        </StatusTabs>

        <SearchRow>
          <SearchInput
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchRow>

        {loading ? (
          <EmptyState>Loading users...</EmptyState>
        ) : users.length === 0 ? (
          <EmptyState>No users found</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Status</Th>
                <Th>Tier</Th>
                <Th>Signed Up</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, i) => (
                <tr key={user._id}>
                  <Td style={{ color: tokens.colors.muted, fontSize: '0.8rem' }}>
                    {user.waitlistPosition || '—'}
                  </Td>
                  <Td>
                    <strong>{user.name || '—'}</strong>
                    {user.isAdmin && <Badge $status="active" style={{ marginLeft: '0.5rem', fontSize: '0.65rem' }}>Admin</Badge>}
                  </Td>
                  <Td style={{ fontSize: '0.85rem' }}>{user.email}</Td>
                  <Td><Badge $status={user.accountStatus || 'active'}>{user.accountStatus || 'active'}</Badge></Td>
                  <Td>
                    {user.accountStatus === 'active' ? (
                      <TierSelect
                        value={user.subscriptionTier || ''}
                        onChange={e => handleTierChange(user._id, e.target.value)}
                        disabled={actionLoading === user._id}
                      >
                        <option value="" disabled>—</option>
                        <option value="starter">Starter</option>
                        <option value="professional">Professional</option>
                        <option value="business">Business</option>
                      </TierSelect>
                    ) : (
                      <TierBadge>{user.subscriptionTier || '—'}</TierBadge>
                    )}
                  </Td>
                  <Td style={{ fontSize: '0.8rem', color: tokens.colors.muted }}>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </Td>
                  <Td>
                    {user.accountStatus === 'pending' && (
                      <ActionBtn
                        $variant="activate"
                        onClick={() => handleActivate(user._id, 'starter')}
                        disabled={actionLoading === user._id}
                      >
                        Activate
                      </ActionBtn>
                    )}
                    {user.accountStatus === 'active' && user._id !== currentUser?.id && (
                      <ActionBtn
                        $variant="suspend"
                        onClick={() => handleSuspend(user._id)}
                        disabled={actionLoading === user._id}
                      >
                        Suspend
                      </ActionBtn>
                    )}
                    {user.accountStatus === 'suspended' && (
                      <ActionBtn
                        $variant="activate"
                        onClick={() => handleActivate(user._id, user.subscriptionTier || 'starter')}
                        disabled={actionLoading === user._id}
                      >
                        Reactivate
                      </ActionBtn>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Container>
    </PageWrapper>
  );
};

export default AdminPage;
